import { EditOutlined } from '@ant-design/icons';
import { TextField, useForm } from '@refinedev/antd';
import { Button, Flex, Form, Skeleton, Tag } from 'antd';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ModalFormActions,
  ShowInfoGrid,
  ShowInfoItem,
} from '@/components/general/ShowInfo/ShowInfo';
import { ShowSection } from '@/components/general/ShowInfo/ShowSection';
import { useCountryContext } from '@/contexts/CountryContext';
import { useUserContext } from '@/contexts/UserContext';
import { USER_WORK_AREA } from '@/model/General/UserWorkArea';
import { SERVICE_ORDER_STATUS, ServiceOrder } from '@/model/ServiceOrder/ServiceOrder';

import ServiceOrderAdminData from '../ServiceOrderForm/ServiceOrderAdminData';
import { formatWithSuffix } from './serviceOrderShowHelpers';

interface ServiceOrderShowAdminProps {
  serviceOrder: ServiceOrder;
  refetch?: () => Promise<any>;
  modifingAdminData?: boolean;
  setModifingAdminData?: (modifying: boolean) => void;
}

const ServiceOrderShowAdmin = (props: ServiceOrderShowAdminProps) => {
  const { userWorkArea } = useUserContext();
  const { isBrasilSelected } = useCountryContext();
  const { t } = useTranslation();
  const { serviceOrder, refetch, modifingAdminData, setModifingAdminData } = props;
  const { kilometres_to_pay, kilometres_rate, hourly_rate, hours_to_pay, currency } = serviceOrder;
  const [saveEdit, setSaveEdit] = useState<boolean>(false);
  const { formProps } = useForm({
    redirect: false,
    action: 'edit',
    resource: 'service_orders',
    id: serviceOrder.id,
    queryOptions: { enabled: false },
    successNotification: {
      message: t('Orden de servicio actualizada correctamente'),
      type: 'success',
    },
    onMutationSuccess: () => {
      void refetch?.().then(() => {
        setSaveEdit(false);
      });
    },
    onMutationError: () => setSaveEdit(false),
  });

  useEffect(() => {
    // Mantiene el form sincronizado con la última versión persistida.
    if (!modifingAdminData) {
      formProps?.form?.setFieldsValue(serviceOrder);
    }
  }, [formProps?.form, modifingAdminData, serviceOrder]);

  // Botón Editar visible solo cuando no estamos editando y el estado/permisos lo permiten.
  const canEdit =
    !modifingAdminData &&
    !saveEdit &&
    serviceOrder.status != SERVICE_ORDER_STATUS.FINALIZADA &&
    (userWorkArea === USER_WORK_AREA.ADMINISTRATION || userWorkArea === USER_WORK_AREA.SOFTWARE);
  const editButton = canEdit ? (
    <Button
      size="small"
      icon={<EditOutlined />}
      onClick={() => {
        formProps?.form?.setFieldsValue(serviceOrder);
        setModifingAdminData?.(true);
      }}>
      {t('Editar')}
    </Button>
  ) : undefined;

  return (
    <ShowSection
      title={t('Detalles de administración')}
      className="ServiceOrderShowAdmin"
      headerExtra={editButton}>
      {modifingAdminData ? (
        <div className="ServiceOrderShowAdminEditMode">
          <Form
            {...formProps}
            initialValues={serviceOrder}
            onFinish={v => {
              setSaveEdit(true);
              formProps?.onFinish?.(v);
              setModifingAdminData?.(false);
            }}
            layout="vertical">
            <ServiceOrderAdminData
              formProps={formProps}
              hideTechnicianReported={isBrasilSelected}
            />
            <ModalFormActions onCancel={() => setModifingAdminData?.(false)} />
          </Form>
        </div>
      ) : (
        <>
          <ShowInfoGrid>
            {!isBrasilSelected && (
              <>
                <ShowInfoItem label={t('Horas informadas por técnico')}>
                  <TextField value={formatWithSuffix(serviceOrder.hours, 'hs')} />
                </ShowInfoItem>
                <ShowInfoItem label={t('Kilómetros informados por técnico')}>
                  <TextField value={formatWithSuffix(serviceOrder.kilometres, 'km')} />
                </ShowInfoItem>
              </>
            )}
            <ShowInfoItem label={t('Horas a pagar')}>
              <TextField value={formatWithSuffix(hours_to_pay, 'hs')} />
            </ShowInfoItem>
            <ShowInfoItem label={t('Kilómetros a pagar')}>
              <TextField value={formatWithSuffix(kilometres_to_pay, 'km')} />
            </ShowInfoItem>
          </ShowInfoGrid>

          {!saveEdit && (
            <>
              <div className="ServiceOrderShowAdminTotal">
                <b>{`${t('TOTAL POR HORAS Y KILÓMETROS')} (${t('sin IVA')})`}</b>
                <span className="ServiceOrderShowAdminTotalValue">
                  {kilometres_to_pay !== undefined &&
                  kilometres_rate !== undefined &&
                  hours_to_pay !== undefined &&
                  hourly_rate !== undefined &&
                  currency !== undefined
                    ? formatWithSuffix(
                        kilometres_to_pay * kilometres_rate + hours_to_pay * hourly_rate,
                        currency?.simbolo,
                      )
                    : '-'}
                </span>
              </div>
              <ShowInfoItem label={t('Números de factura')}>
                <Flex gap={4} wrap>
                  {serviceOrder.billing_numbers
                    ?.split(',')
                    .filter((n: string) => n.trim())
                    .map((number: string) => <Tag key={number.trim()}>{number.trim()}</Tag>) || '-'}
                </Flex>
              </ShowInfoItem>
            </>
          )}
          {saveEdit && <Skeleton active />}
        </>
      )}
    </ShowSection>
  );
};

export default ServiceOrderShowAdmin;
